import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Trash2 } from 'lucide-react';
import DoctorShell from '../components/DoctorShell';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROUTES } from '../../constants/routes';
import { COUNTRIES, SPECIALTIES } from '../constants/doctorPrompts';
import {
  doctorCancelSubscription,
  doctorChangePassword,
  doctorUpdateMe,
} from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';
import { compressClinicLogoFile } from '../utils/compressClinicLogo';

const fieldClass =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      {children}
    </label>
  );
}

export default function DoctorSettingsScreen() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t, saveLanguage } = useLanguage();
  const { doctor, refreshDoctor, showToast, trialRemaining } = useDoctorSession();
  const logoInputRef = useRef(null);
  const [form, setForm] = useState({
    full_name: '',
    specialty: '',
    clinic_name: '',
    clinic_logo_url: '',
    country: '',
    phone: '',
    language_preference: 'ar_en',
    default_note_format: 'soap',
  });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!doctor) return;
    setForm({
      full_name: doctor.full_name || '',
      specialty: doctor.specialty || 'General Practice',
      clinic_name: doctor.clinic_name || '',
      clinic_logo_url: doctor.clinic_logo_url || '',
      country: doctor.country || 'Jordan',
      phone: doctor.phone || '',
      language_preference: doctor.language_preference || 'ar_en',
      default_note_format: doctor.default_note_format || 'soap',
    });
  }, [doctor]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const saveAll = async () => {
    setSaving(true);
    setError('');
    try {
      await doctorUpdateMe(form);
      await refreshDoctor();
      if (form.language_preference === 'ar') saveLanguage('ar');
      else if (form.language_preference === 'en') saveLanguage('en');
      showToast(t('doctorSettingsSaved'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    setError('');
    try {
      const dataUrl = await compressClinicLogoFile(file);
      setForm((f) => ({ ...f, clinic_logo_url: dataUrl }));
    } catch (err) {
      setError(err.message || t('doctorLogoUploadFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const changePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) return;
    setPwBusy(true);
    setError('');
    try {
      await doctorChangePassword(passwords);
      setPasswords({ current_password: '', new_password: '' });
      showToast(t('doctorPasswordChanged'));
    } catch (err) {
      setError(err.message);
    } finally {
      setPwBusy(false);
    }
  };

  const cancelSub = async () => {
    try {
      await doctorCancelSubscription('user_cancelled');
      await refreshDoctor();
      showToast(t('doctorSubscriptionCancelled'));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <DoctorShell>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.DOCTOR_PATIENTS)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
            aria-label={t('back')}
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <h1 className="text-xl font-bold text-slate-900">{t('doctorSettings')}</h1>
        </div>
      </header>

      <div className="space-y-5 px-4 py-4 pb-28">
        {error && (
          <div className="rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
            {error}
          </div>
        )}

        <section className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">{t('doctorProfile')}</h2>

          <Field label={t('doctorFullName')}>
            <input
              value={form.full_name}
              onChange={set('full_name')}
              className={fieldClass}
              placeholder={t('doctorFullName')}
            />
          </Field>

          <Field label="Email">
            <input
              value={doctor?.email || ''}
              readOnly
              className={`${fieldClass} bg-gray-50 text-gray-500`}
              dir="ltr"
            />
          </Field>

          <Field label={t('doctorSpecialty')}>
            <select value={form.specialty} onChange={set('specialty')} className={fieldClass}>
              {SPECIALTIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('doctorClinicName')}>
            <input
              value={form.clinic_name}
              onChange={set('clinic_name')}
              className={fieldClass}
              placeholder={t('doctorClinicNamePlaceholder')}
            />
          </Field>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-500">{t('doctorClinicLogo')}</p>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50 ring-1 ring-gray-100">
                {form.clinic_logo_url ? (
                  <img src={form.clinic_logo_url} alt="" className="h-full w-full object-contain" />
                ) : (
                  <ImagePlus className="h-6 w-6 text-gray-300" strokeWidth={2} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoPick}
                />
                <button
                  type="button"
                  disabled={logoBusy}
                  onClick={() => logoInputRef.current?.click()}
                  className="rounded-lg bg-primary-light px-3 py-2 text-sm font-semibold text-primary disabled:opacity-60"
                >
                  {logoBusy ? '…' : t('doctorUploadLogo')}
                </button>
                {form.clinic_logo_url ? (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, clinic_logo_url: '' }))}
                    className="mt-2 flex items-center gap-1 text-xs font-semibold text-critical"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    {t('doctorRemoveLogo')}
                  </button>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-400">{t('doctorLogoHint')}</p>
                )}
              </div>
            </div>
          </div>

          <Field label={t('doctorCountry')}>
            <select value={form.country} onChange={set('country')} className={fieldClass}>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('doctorPhoneOptional')}>
            <input
              value={form.phone}
              onChange={set('phone')}
              className={fieldClass}
              placeholder={t('doctorPhoneOptional')}
              dir="ltr"
            />
          </Field>
        </section>

        <div className="border-t border-gray-100" />

        <section className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">{t('doctorPreferences')}</h2>
          <Field label={t('language') || 'Language'}>
            <select
              value={form.language_preference}
              onChange={set('language_preference')}
              className={fieldClass}
            >
              <option value="ar">Arabic</option>
              <option value="en">English</option>
              <option value="ar_en">Both</option>
            </select>
          </Field>
          <Field label={t('doctorNoteFormat')}>
            <select
              value={form.default_note_format}
              onChange={set('default_note_format')}
              className={fieldClass}
            >
              <option value="soap">SOAP</option>
              <option value="narrative">Narrative</option>
            </select>
          </Field>
        </section>

        <div className="border-t border-gray-100" />

        <section className="space-y-2">
          <h2 className="text-base font-bold text-slate-900">{t('doctorSubscription')}</h2>
          <div className="rounded-xl bg-primary-light/60 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">
              {doctor?.subscription_status === 'trial'
                ? `${t('doctorTrial')}: ${trialRemaining} ${t('doctorRemaining')}`
                : `Pro · ${doctor?.subscription_plan || ''}`}
            </p>
            {doctor?.subscription_status === 'trial' && (
              <p className="mt-1 text-xs text-gray-600">
                {t('doctorNotesUsed')}: {doctor.trial_notes_used}/{doctor.trial_notes_limit}
              </p>
            )}
            {doctor?.subscription_end_date && (
              <p className="mt-1 text-xs text-gray-600">
                {t('doctorNextBilling')}: {doctor.subscription_end_date}
              </p>
            )}
          </div>
          {doctor?.subscription_status === 'active' && (
            <button type="button" onClick={cancelSub} className="text-sm font-semibold text-critical">
              {t('doctorCancelSubscription')}
            </button>
          )}
          {doctor?.subscription_status === 'trial' && trialRemaining <= 0 && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.DOCTOR_PAYWALL)}
              className="btn-primary"
            >
              {t('doctorSubscribeMonthly')}
            </button>
          )}
        </section>

        <div className="border-t border-gray-100" />

        <section className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">{t('doctorAccount')}</h2>
          <Field label={t('doctorCurrentPassword')}>
            <input
              type="password"
              value={passwords.current_password}
              onChange={(e) => setPasswords((p) => ({ ...p, current_password: e.target.value }))}
              className={fieldClass}
              autoComplete="current-password"
            />
          </Field>
          <Field label={t('doctorNewPassword')}>
            <input
              type="password"
              value={passwords.new_password}
              onChange={(e) => setPasswords((p) => ({ ...p, new_password: e.target.value }))}
              className={fieldClass}
              autoComplete="new-password"
            />
          </Field>
          <button
            type="button"
            onClick={changePassword}
            disabled={pwBusy || !passwords.current_password || !passwords.new_password}
            className="w-full rounded-xl border border-gray-200 py-3 text-sm font-semibold text-primary disabled:opacity-50"
          >
            {pwBusy ? '…' : t('doctorChangePassword')}
          </button>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate(ROUTES.LOGIN, { replace: true });
            }}
            className="w-full rounded-xl py-3 text-sm font-semibold text-critical"
          >
            {t('signOut')}
          </button>
        </section>
      </div>

      <div className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] left-0 right-0 z-40 border-t border-gray-200 bg-surface px-4 py-3">
        <button
          type="button"
          onClick={saveAll}
          disabled={saving || logoBusy}
          className="btn-primary disabled:opacity-60"
        >
          {saving ? '…' : t('save')}
        </button>
      </div>
    </DoctorShell>
  );
}
